import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useInboxPanel } from '../hooks';
import { FONT_ACTION, ITEM_META_FONT_SIZE, PANEL_MAX_HEIGHT, PANEL_WIDTH } from '../designTokens';
import { NotificationItem } from './NotificationItem';
import { NotificationPanelHeader } from './NotificationPanelHeader';

export interface NotificationPanelProps {
  onClose: () => void;
  /** Lets the bell's badge settle on an action's own response (SPEC "Edge cases"). */
  onUnreadChange: (unread: number) => void;
  /** The popover needs a fixed width; the sheet fills its drawer instead (AC-31). */
  variant: 'popover' | 'sheet';
}

function clearedCopy(noun: string, count: number): string {
  return `Cleared ${count} ${noun}${count === 1 ? '' : 's'}.`;
}

/**
 * The bell panel's body (IAN-26, IAN-28, IAN-29, IAN-53).
 *
 * `NotificationBell` mounts this component ONLY while the popover/sheet is
 * open — so mounting IS opening. That is what lets `useInboxPanel`'s
 * read-on-open guarantee (AC-13) be a plain one-shot effect rather than
 * something keyed off a boolean prop: every mount of this component is a
 * fresh open, and every unmount is a close.
 *
 * Loading, error and empty are three distinct sentences (AC-30, AC-R4,
 * AC-E7) — never a shared blank box, and never each other.
 */
export function NotificationPanel({ onClose, onUnreadChange, variant }: NotificationPanelProps) {
  const inbox = useInboxPanel(onUnreadChange);
  const [clearedMessage, setClearedMessage] = useState<string | null>(null);

  const handleClearRead = () => {
    setClearedMessage(null);
    void inbox.clearRead().then((count) => setClearedMessage(clearedCopy('read notification', count)));
  };

  const handleClearOlderThanAMonth = () => {
    setClearedMessage(null);
    void inbox
      .clearOlderThanAMonth()
      .then((count) => setClearedMessage(clearedCopy('notification older than a month', count)));
  };

  const hasUnread = inbox.items.some((item) => !inbox.isRead(item));
  const isSheet = variant === 'sheet';

  return (
    <Box
      role="dialog"
      aria-label="Notifications"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: isSheet ? '100%' : PANEL_WIDTH,
        maxHeight: isSheet ? '80vh' : PANEL_MAX_HEIGHT,
      }}
    >
      <NotificationPanelHeader
        hasUnread={hasUnread}
        markingAll={inbox.markingAll}
        disableBulkActions={inbox.items.length === 0 || inbox.dismissing}
        onMarkAllRead={() => void inbox.markAllRead()}
        onClearRead={handleClearRead}
        onClearOlderThanAMonth={handleClearOlderThanAMonth}
        onClose={onClose}
      />

      {inbox.markError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {inbox.markError}
        </Alert>
      )}
      {inbox.dismissError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {inbox.dismissError}
        </Alert>
      )}
      {clearedMessage && (
        <Alert severity="success" sx={{ borderRadius: 0 }} onClose={() => setClearedMessage(null)}>
          {clearedMessage}
        </Alert>
      )}

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {inbox.loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: FONT_ACTION, color: 'text.secondary' }}>
              Loading your notifications…
            </Typography>
          </Box>
        ) : inbox.error ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: FONT_ACTION, color: 'text.secondary' }}>
              We could not load your notifications.
            </Typography>
            <Button size="small" onClick={() => void inbox.reload()} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          </Box>
        ) : inbox.items.length === 0 ? (
          // Covers both AC-E7 (nothing ever received) and AC-R4 (no linked
          // employee record) — the API returns an empty list for both, and
          // the copy is honest for either reading rather than guessing which.
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: FONT_ACTION, fontWeight: 600, color: 'text.secondary' }}>
              You are all caught up
            </Typography>
            <Typography sx={{ fontSize: ITEM_META_FONT_SIZE, color: 'text.disabled', mt: 0.5 }}>
              Timesheet reminders, allocation changes and project updates will appear here as they
              happen.
            </Typography>
          </Box>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {inbox.items.map((item) => (
              <NotificationItem
                key={item.id}
                item={item}
                read={inbox.isRead(item)}
                dismissDisabled={inbox.dismissing}
                onDismiss={() => void inbox.dismissOne(item.id)}
                onNavigate={onClose}
              />
            ))}
            {inbox.hasMore && (
              <Box sx={{ p: 1.5, textAlign: 'center' }}>
                <Button
                  size="small"
                  onClick={() => void inbox.loadMore()}
                  disabled={inbox.loadingMore}
                  sx={{ textTransform: 'none' }}
                >
                  {inbox.loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default NotificationPanel;
